import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useComparisonStore } from '@/stores/comparison-store'
import { toastSuccess } from '@/stores'
import { useQuoteDraftStore } from '@/stores/quote-draft-store'
import type { RankedOffer } from '@/modules/comparison/types'

const QUERY_KEY = ['comparison'] as const

export function useComparison() {
  const queryClient = useQueryClient()
  const hydrate = useComparisonStore((s) => s.hydrate)
  const ready = useComparisonStore((s) => s.ready)
  const query = useComparisonStore((s) => s.query)
  const filters = useComparisonStore((s) => s.filters)
  const sortBy = useComparisonStore((s) => s.sortBy)
  const groups = useComparisonStore((s) => s.groups)
  const flat = useComparisonStore((s) => s.flat)
  const summary = useComparisonStore((s) => s.summary)
  const selectedIds = useComparisonStore((s) => s.selectedIds)
  const favorites = useComparisonStore((s) => s.favorites)
  const history = useComparisonStore((s) => s.history)
  const statistics = useComparisonStore((s) => s.statistics)
  const weights = useComparisonStore((s) => s.weights)
  const setQuery = useComparisonStore((s) => s.setQuery)
  const setFilters = useComparisonStore((s) => s.setFilters)
  const setSortBy = useComparisonStore((s) => s.setSortBy)
  const runSearch = useComparisonStore((s) => s.runSearch)
  const toggleSelect = useComparisonStore((s) => s.toggleSelect)
  const clearSelection = useComparisonStore((s) => s.clearSelection)
  const toggleFavorite = useComparisonStore((s) => s.toggleFavorite)
  const saveWeights = useComparisonStore((s) => s.saveWeights)

  const addFromProduct = useQuoteDraftStore((s) => s.addFromProduct)

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      hydrate()
      if (!useComparisonStore.getState().flat.length) {
        runSearch('notebook')
      }
      return useComparisonStore.getState().summary
    },
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const search = useCallback(
    (term: string) => {
      runSearch(term)
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    [runSearch, queryClient],
  )

  const addToQuote = useCallback(
    (offer: RankedOffer) => {
      addFromProduct({
        productId: offer.id,
        codigoInterno: offer.sku,
        descricao: `${offer.description} · ${offer.supplier}`,
        valorManual: offer.pixPrice,
        quantity: 1,
        imagem: offer.imageUrl ?? null,
        source: 'search',
      })
      toastSuccess('Adicionado ao orçamento', offer.supplier)
    },
    [addFromProduct],
  )

  const favorite = useCallback(
    (supplier: string) => {
      const wasFavorite = useComparisonStore.getState().favorites.includes(supplier)
      toggleFavorite(supplier)
      toastSuccess(wasFavorite ? 'Favorito removido' : 'Fornecedor favoritado', supplier)
    },
    [toggleFavorite],
  )

  const selectedOffers = flat.filter((o) => selectedIds.includes(o.id))

  return {
    ready,
    query,
    setQuery,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    groups,
    flat,
    summary,
    selectedIds,
    selectedOffers,
    toggleSelect,
    clearSelection,
    favorites,
    history,
    statistics,
    weights,
    saveWeights,
    search,
    addToQuote,
    favorite,
    isLoading: listQuery.isLoading,
  }
}
